import { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Stack, Alert, Typography, FormControlLabel, Checkbox,
} from "@mui/material";
import { BillingItem, sendInvoiceEmail } from "../services/billing";

interface Props {
  open:    boolean;
  item:    BillingItem | null;
  onClose: () => void;
  onDone:  (msg: string, receptorUpdated: boolean) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** CP-157: enviar copia del PDF de una factura ya emitida a un email nuevo,
 *  con la opción (default) de agregarlo al receptor para próximas facturas. */
export default function SendCopyEmailDialog({ open, item, onClose, onDone }: Props) {
  const [email, setEmail] = useState("");
  const [save,  setSave]  = useState(true);
  const [busy,  setBusy]  = useState(false);
  const [err,   setErr]   = useState<string | null>(null);

  useEffect(() => {
    if (open) { setEmail(""); setSave(true); setBusy(false); setErr(null); }
  }, [open]);

  const valid = EMAIL_RE.test(email.trim());

  async function submit() {
    if (!item || !valid) return;
    setBusy(true); setErr(null);
    try {
      const r = await sendInvoiceEmail(item.period, item.collector,
        { copyTo: email.trim(), saveToReceptor: save });
      const saved = r.receptor_updated === true;
      onDone(
        `Copia enviada a ${r.recipient ?? email.trim()}`
          + (saved ? " · email agregado al receptor" :
             r.receptor_updated === "ya_estaba" ? " · el email ya estaba en el receptor" : ""),
        saved,
      );
    } catch (e: any) {
      setErr(String(e?.message ?? e));
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        Enviar copia a otro email
        <Typography variant="body2" color="text.secondary">
          {item?.collector} · factura {item?.invoice_number ?? "emitida"}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField label="Email nuevo" type="email" value={email} autoFocus
            onChange={(e) => setEmail(e.target.value)} disabled={busy}
            onKeyDown={(e) => e.key === "Enter" && valid && submit()}
            error={email.length > 0 && !valid}
            helperText={email.length > 0 && !valid ? "email inválido" : " "} />
          <FormControlLabel
            control={<Checkbox checked={save} disabled={busy}
              onChange={(e) => setSave(e.target.checked)} />}
            label={<Typography variant="body2">
              Agregar al receptor (las próximas facturas también van a este email)
            </Typography>} />
          <Alert severity="info" sx={{ py: 0.5 }}>
            La copia del PDF va <b>solo</b> al email nuevo — los destinatarios que ya la
            recibieron no vuelven a recibirla.
          </Alert>
          {err && <Alert severity="error">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancelar</Button>
        <Button onClick={submit} variant="contained" disabled={busy || !valid}>
          {busy ? "Enviando…" : "Enviar copia"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
